import React, { useEffect, useState, useRef, useContext } from 'react'
import { FormFieldLabel } from 'components/spreadsheet/components/form/fieldLabel'
import { IColumnTypes, IColumnValuesCount } from 'components/spreadsheet/types'
import {
  getRowHeightVariable,
  kindOptionsToSave,
  resolveDependencyCachedOptions,
  sourceValuesOf,
  isFieldValueEmpty
} from 'components/spreadsheet/helpers/functions'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import {
  ICellValue,
  IContextMenuState,
  ITableRow,
  ITableViewColour,
  ITableViewColumn,
  IRowLevelColour,
  SelectKindOptions
} from 'types'
import { DataContext } from 'components/spreadsheet/contexts/data/DataContext'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { ProjectContext } from 'hooks/projectContext'
import { ApplicationContext } from 'hooks/application'
import { getCellColour } from 'components/spreadsheet/helpers/colouring'
import Button from 'components/button'
import { ChevronDown, Triangle } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import SelectMenu, { fittedMenuWidth } from 'components/select/components/menu'
import { checkValuesSame } from 'components/spreadsheet/helpers/paste'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import { useThemedColours } from 'hooks/useTheme'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'

// The heading over a linked column's hand-entered options in its dropdown.
const MANUAL_INPUTS_GROUP = 'Missing from source table'

type SelectCellOption = { value: string; label: string; group?: string }

interface SelectCellProps {
  value: string | string[]
  align: 'center' | 'left' | 'right'
  color: string
  kind: IColumnTypes
  readOnly: boolean
  isAdmin: boolean
  isContributor: boolean
  rowHeight: number
  selected: boolean
  isExpanded?: boolean
  validation: boolean
  isJoined: boolean
  editing?: boolean
  onCloseSelect?: () => void
  formValues?: Record<string, ICellValue>
  handleKeyDown?: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | string[], onSuccess: () => void, onError?: (message?: string) => void) => void
  type: ViewTypes
  column: ITableViewColumn | null
  colourSettings: ITableViewColour[]
  rowData?: {
    [columnId: string]: ICellValue
  }
  columnValuesCount: IColumnValuesCount
  row: ITableRow | null
  rowLevelColour?: IRowLevelColour | null
}

const styles = cellStyles()

// An option chosen, as the grid shows it: a small bordered chip.
const GRID_VALUE = 'whitespace-pre-line bg-surface-sunken rounded h-fit border border-divider m-0.5 py-0 px-[3px]'
// The fill an option's chip has when no colour rule gives it one (getCellColour's grey background).
const UNCOLOURED_OPTION_FILL = 'var(--color-surface-sunken)'
// In a form, a dropdown's value reads as a field's text does: plain, unless its option has a colour.
const FORM_VALUE = 'whitespace-pre-line'
// Several chosen, or one with a colour: soft pills without a border, as the field's text is set.
const FORM_VALUE_PILL = 'whitespace-pre-line rounded-md bg-surface-sunken h-fit my-0.5 mr-1 px-1.5 leading-5'

// As wide as the menus that pick from a list elsewhere (Edit column's Add option), and wider for options that
// need it, up to a point; past that an option is cut off, with the whole of it on hover.
const SELECT_CELL_MENU_WIDTH = 260
const VIEWPORT_GAP = 8

/** How wide a select cell's option list opens: room for its longest option, as every list fits its own, and
 * never narrower than the usual width. */
export const selectCellMenuWidth = (labels: string[]): number =>
  Math.max(SELECT_CELL_MENU_WIDTH, fittedMenuWidth(labels.map((label) => ({ label, value: label }))))

/**
 * Where a select cell's option list starts, from the cell's box: under the cell, from its left
 * edge. Where the list wouldn't fit to the right of that, it lines up with the cell's right edge
 * instead, as a popover does, rather than being pushed back into the window until it fits, which
 * left it well to the left of its cell and flush against the window's edge.
 */
export const selectCellMenuLeft = (
  cell: DOMRect,
  viewportWidth: number = window.innerWidth,
  width: number = SELECT_CELL_MENU_WIDTH
): number => {
  if (cell.left + width <= viewportWidth - VIEWPORT_GAP) return cell.left
  return Math.max(VIEWPORT_GAP, cell.right - width)
}

const SelectCellComponent: React.FC<SelectCellProps> = ({
  value,
  align,
  color,
  kind,
  readOnly,
  rowHeight,
  selected,
  isExpanded,
  isAdmin,
  isContributor,
  isJoined,
  editing,
  formValues,
  validation,
  handleKeyDown,
  setCellValue,
  column,
  type,
  colourSettings,
  rowData,
  columnValuesCount,
  row,
  onCloseSelect,
  rowLevelColour
}) => {
  // In a form, named as the field it's drawn in.
  const labelledBy = useContext(FormFieldLabel)
  const themed = useThemedColours()
  const inputRef = useRef<HTMLDivElement>(null)
  const selectionTimeout = useRef<TimeoutID | null>(null)
  const pendingCreatedValueRef = useRef<string | null>(null)
  const [selectCellMenu, setSelectCellMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const dataContext = useContext(DataContext)
  const projectContext = useContext(ProjectContext)
  const applicationContext = useContext(ApplicationContext)

  const kindOptions = column && column.kindOptions
  const [currentValue, setCurrentValue] = useState<string | string[]>(value != null ? value : '')

  // Inline option creation is only for manually-managed select columns (no
  // picklist source column) with the setting enabled, and only for editors in the grid
  const canCreateOption =
    !!dataContext &&
    !!projectContext &&
    type !== ViewTypes.FORM &&
    kindOptions?.expandOptionsViaCellEntry !== false &&
    !kindOptions?.tableOptions?.columnId &&
    !!column &&
    (isAdmin || isContributor)

  useEffect(() => {
    const newValue = value != null ? value : ''
    if (!checkValuesSame(newValue, currentValue, kind)) {
      setCurrentValue(newValue)
    }
  }, [value])

  // A value in this row (or this form) that the options depend on.
  const valueOf = (columnId: string): ICellValue | undefined => {
    if (type === ViewTypes.FORM) {
      return formValues ? formValues[columnId] : undefined
    }
    return rowData ? rowData[columnId] : undefined
  }

  const allOptions = (() => {
    let cachedOptions: string[]
    if (kindOptions?.tableOptions?.dependencies?.length) {
      const cachedOptionsDict = kindOptions?.tableOptions?.cachedOptions
        ? (kindOptions.tableOptions.cachedOptions as { [key: string]: string[] })
        : {}
      cachedOptions = resolveDependencyCachedOptions(cachedOptionsDict, kindOptions.tableOptions.dependencies, valueOf)
    } else {
      cachedOptions = (kindOptions?.tableOptions?.cachedOptions as string[]) || []
    }

    // Hand-entered options are offered in every row. One this row's source options already hold is listed once, as
    // the source's. One the source has under another dependency value isn't missing from it, so it follows the
    // source's without a heading; only one the source has nowhere goes under the Missing heading, as Edit column
    // counts them.
    const inSource = sourceValuesOf(kindOptions?.tableOptions?.cachedOptions)
    const offered = new Set<string>(cachedOptions)
    const manualOptions = (kindOptions?.manualOptions || []).filter((option: string) => !offered.has(option))
    const linked = !!kindOptions?.tableOptions?.columnId
    return [
      ...cachedOptions.map((option: string): SelectCellOption => ({ value: option, label: option })),
      ...manualOptions
        .filter((option: string) => inSource.has(option))
        .map((option: string): SelectCellOption => ({ value: option, label: option })),
      ...manualOptions
        .filter((option: string) => !inSource.has(option))
        .map(
          (option: string): SelectCellOption => ({
            value: option,
            label: option,
            // With a linked table, the hand-entered options it doesn't have sit under a heading of their own after
            // the table's, so they don't read as the table's.
            group: linked ? MANUAL_INPUTS_GROUP : undefined
          })
        )
    ]
  })()
  const hasManualGroup = allOptions.some((option) => option.group === MANUAL_INPUTS_GROUP)

  // In a form the field is a dropdown, as a select is elsewhere: a click anywhere on it opens the options,
  // and a chevron at its end says it will.
  const asFormField = !!isExpanded && type === ViewTypes.FORM

  // Measured as the list opens, not on every render of every cell.
  const [menuWidth, setMenuWidth] = useState<number>(SELECT_CELL_MENU_WIDTH)
  const menuLeftFor = (cell: DOMRect): number => {
    // In a form, at least as wide as the field, as a dropdown's list is.
    const fitted = selectCellMenuWidth(allOptions.map((option) => option.label))
    const width = asFormField ? Math.max(fitted, Math.round(cell.width)) : fitted
    setMenuWidth(width)
    return selectCellMenuLeft(cell, window.innerWidth, width)
  }

  // An option's own fill in a form, when a colour rule gives it one; options without one have the grey the grid's chips
  // are filled with, which a form leaves out.
  const formFill = (colour: ReturnType<typeof getCellColour> | null | undefined): string | undefined =>
    colour && colour.backgroundColour !== UNCOLOURED_OPTION_FILL ? themed.fill(colour.backgroundColour) : undefined

  const cellColours = (() => {
    if (!column) return null
    const values = Array.isArray(currentValue)
      ? (currentValue as string[])
      : currentValue
        ? [currentValue.toLocaleString()]
        : []
    const map = new Map<string, ReturnType<typeof getCellColour>>()
    for (const val of values) {
      if (!map.has(val)) {
        map.set(
          val,
          getCellColour(column, columnValuesCount, row, val, colourSettings, true, undefined, rowLevelColour, rowData)
        )
      }
    }
    return map
  })()

  useEffect(() => {
    if (!selected) {
      setSelectCellMenu(INITIAL_CONTEXT_MENU_STATE)
    }
  }, [selected])

  // Open the menu when the cell transitions into editing mode (e.g. Space/Enter
  // from the cell wrapper). Anchoring off the cell-value bounding box keeps the
  // menu in the right place without dispatching a synthetic dblclick.
  // Functional update + early-return-if-already-open so we never re-overwrite
  // a menu the dropdown button just opened.
  useEffect(() => {
    if (editing && !readOnly && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      const left = menuLeftFor(rect)
      setSelectCellMenu((prev) => {
        if (prev.open) return prev
        return {
          ...prev,
          open: true,
          top: `${rect.bottom}px`,
          left: `${left}px`,
          bottom: 'auto',
          right: 'auto'
        }
      })
    }
  }, [editing])

  const handleSetCellValue = (newValue: string | string[]) => {
    if (setCellValue && (isAdmin || isContributor)) {
      // Capture the last-committed value so we can roll back the optimistic
      // update if the save is rejected.
      const previousValue = value != null ? value : ''
      setCurrentValue(newValue)
      if (selectionTimeout.current !== null) cancelTimeout(selectionTimeout.current)
      selectionTimeout.current = requestTimeout(
        () => {
          setCellValue(
            newValue,
            () => {
              return null
            },
            () => {
              // Validation rejected the change (e.g. a no-duplicates column with hard
              // validation). Rollback the optimistic update to the last-committed value.
              setCurrentValue(previousValue)
            }
          )
        },
        kind === 'multiselect' ? 1000 : 250
      )
    }
  }

  const handleOptionSelected = (selectedOption: string) => {
    if (kind === 'select') {
      // if selected option is the same as the current value of the cell we need to clear the cell
      const newValue = selectedOption === currentValue ? '' : selectedOption
      handleSetCellValue(newValue)
      if (onCloseSelect) onCloseSelect()
    } else if (kind === 'multiselect') {
      const arrSelectedValues = currentValue ? [...(currentValue as string[])] : []

      // Remove any invalid values
      const cleanedSelectedValues = arrSelectedValues.filter(
        (selectedValue) => !!allOptions.find((option) => selectedValue === option.value)
      )

      // Check wheater the selected option is not part of the current array of values
      // if it is already there, we need to remove it.
      const newSelectedValues = cleanedSelectedValues.filter((val) => val !== selectedOption)

      // If the selected option was not part of the array of values we need to add it
      if (newSelectedValues.length === cleanedSelectedValues.length) {
        newSelectedValues.push(selectedOption)
      }
      handleSetCellValue(newSelectedValues)
    }
  }

  const handleCreateOption = (rawValue: string) => {
    const newValue = rawValue.trim()
    if (!newValue || !column || !dataContext || !projectContext) return

    // If the exact value already exists (case-sensitive, matching manual-option
    // behaviour where "Another Test" and "another test" are distinct), apply it
    // rather than adding a duplicate.
    const existing = allOptions.find((option) => option.value.trim() === newValue)
    if (existing) {
      handleOptionSelected(existing.value)
      return
    }

    // Saved as Edit column saves them, then with the new option.
    const newKindOptions = kindOptionsToSave(column.kindOptions ?? {}) as SelectKindOptions
    newKindOptions.manualOptions = [...(column.kindOptions?.manualOptions ?? []), newValue]

    updateColumn(
      projectContext.project.publicId,
      'kindOptions',
      dataContext.spreadsheetData,
      dataContext.setSpreadsheetData,
      column.publicId,
      newKindOptions,
      () => {
        pendingCreatedValueRef.current = newValue
      },
      (error) => applicationContext?.displayErrorMessage(error)
    )
  }

  // Apply a freshly-created option to the cell once it appears in the column's
  // options, so the value validates as an allowed select value. The ref guard
  // means this only acts on the render after creation, when allOptions gains the
  // new value
  useEffect(() => {
    const pending = pendingCreatedValueRef.current
    if (pending != null && allOptions.some((option) => option.value === pending)) {
      pendingCreatedValueRef.current = null
      handleOptionSelected(pending)
    }
  }, [allOptions, handleOptionSelected])

  const isArray = Array.isArray(currentValue)
  const wrapTextAlignment = !isArray
    ? 'center'
    : (currentValue as string[]).length <= (rowHeight ? rowHeight * 3 : 1)
      ? 'center'
      : 'stretch'

  // Under the field's frame, 4px below it and lined up with its edges.
  const openMenuUnder = (element: HTMLElement) => {
    const frame = (element.closest('.form-field-cell') as HTMLElement | null) ?? element
    const rect = frame.getBoundingClientRect()
    setSelectCellMenu((prev) => ({
      ...prev,
      open: true,
      top: `${rect.bottom + 4}px`,
      left: `${menuLeftFor(rect)}px`,
      bottom: 'auto',
      right: 'auto'
    }))
  }

  // Why there's nothing to choose. Options that depend on another field come once it's filled in, so say which.
  const noOptionsMessage = (): React.ReactNode => {
    const tableOptions = kindOptions?.tableOptions
    const unmet = tableOptions?.columnId
      ? tableOptions.dependencies?.find((dependency) => {
          const dependsOn = dataContext?.spreadsheetData?.viewDetails?.columns?.find(
            (viewColumn) => viewColumn.publicId === dependency.columnId
          )
          return !!dependsOn && isFieldValueEmpty(dependsOn.kind, valueOf(dependency.columnId))
        })
      : undefined
    const dependsOn = unmet
      ? dataContext?.spreadsheetData?.viewDetails?.columns?.find((viewColumn) => viewColumn.publicId === unmet.columnId)
      : undefined

    if (dependsOn) {
      return (
        <span>
          Choose a value in{' '}
          <span className="inline-flex items-center gap-1 align-bottom font-medium text-body">
            <IconSelector kind={dependsOn.kind} />
            {dependsOn.name}
          </span>{' '}
          first. The options here depend on it.
        </span>
      )
    }
    return (
      <span className="flex flex-col gap-0.5">
        <span className="font-medium text-body">No options available</span>
        <span>
          {tableOptions?.columnId ? 'Check if the linked table has data.' : 'Add options to this column first.'}
        </span>
      </span>
    )
  }

  return (
    <div
      className={styles.value({
        focused: selected,
        class: `cell-value ${asFormField && !readOnly ? 'cursor-pointer' : ''}`
      })}
      data-focussed={selected || undefined}
      // The field is the menu's control: pressed while the menu is open, it closes it.
      role={asFormField ? 'combobox' : undefined}
      aria-labelledby={labelledBy}
      aria-expanded={asFormField ? selectCellMenu.open : undefined}
      aria-haspopup={asFormField ? 'listbox' : undefined}
      ref={inputRef}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)',
        boxSizing: 'border-box',
        maxHeight: selected && isExpanded ? 'unset' : `${getRowHeightVariable(rowHeight) - 2}px`,
        overflow: selected && isExpanded ? 'visible' : selected ? 'auto' : 'hidden',
        position: selected && isExpanded ? 'relative' : undefined,
        zIndex: selected && isExpanded ? 2 : undefined
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        event.persist()

        if (event.key === ' ' && !selectCellMenu.open) {
          event.preventDefault()
          setSelectCellMenu({
            ...selectCellMenu,
            open: true,
            top: `${event.currentTarget.getBoundingClientRect().bottom}px`,
            left: `${menuLeftFor(event.currentTarget.getBoundingClientRect())}px`,
            bottom: 'auto',
            right: 'auto'
          })
        } else {
          if (handleKeyDown) handleKeyDown(event)
        }
      }}
      onClick={(event: React.MouseEvent) => {
        if (readOnly) {
          isJoined ? event.persist() : event.stopPropagation()
        } else {
          event.persist()
        }
      }}
      // On the press rather than the click: leaving a field being typed in saves it and redraws the form,
      // which would lose the click. Once the press is over, though: opened during it, the menu would hear the
      // same press as one outside it and close at once (issue #10013).
      onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
        if (!asFormField || readOnly || event.button !== 0 || selectCellMenu.open) return
        const field = event.currentTarget
        window.setTimeout(() => openMenuUnder(field), 0)
      }}
      onDoubleClick={(event: React.MouseEvent) => {
        if (readOnly) {
          event.stopPropagation()
          return
        }
        event.persist()
        setSelectCellMenu({
          ...selectCellMenu,
          open: true,
          top: `${event.currentTarget.getBoundingClientRect().bottom}px`,
          left: `${menuLeftFor(event.currentTarget.getBoundingClientRect())}px`,
          bottom: 'auto',
          right: 'auto'
        })
      }}
    >
      <div
        data-testid="select-cell-inner"
        className="flex content-center"
        style={{
          maxHeight: selected && isExpanded ? 'unset' : `${getRowHeightVariable(rowHeight) - 3}px`,
          overflowY: selected && isExpanded ? 'visible' : selected ? 'auto' : 'hidden',
          alignItems: wrapTextAlignment,
          width: '100%'
        }}
      >
        {(selected || isExpanded) && !readOnly && !asFormField && (
          <Button
            className="p-0.5 mr-[3px] max-w-[27px] w-[27px] h-[30px]"
            internalType="grey"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              const rect = event.currentTarget.getBoundingClientRect()
              // Lined up with the cell, not just this button, when it runs out of room.
              const cell = inputRef.current?.getBoundingClientRect() ?? rect
              const left = menuLeftFor(cell)
              setSelectCellMenu((prev) => ({
                ...prev,
                open: true,
                top: `${rect.bottom}px`,
                left: `${left}px`,
                bottom: 'auto',
                right: 'auto'
              }))
            }}
          >
            <Triangle type="down" className="h-3 w-3" />
          </Button>
        )}
        {currentValue == null || currentValue === '' || (isArray && (currentValue as string[]).length === 0) ? (
          <span>&nbsp;</span>
        ) : kind === 'select' ? (
          <span
            id="select-value-list"
            className={styles.content({
              validation,
              class: 'flex flex-wrap p-0'
            })}
          >
            {(() => {
              const cellColour = cellColours?.get(currentValue.toLocaleString()) ?? null
              return (
                <span
                  id="select-value-item"
                  data-testid="select-value-item"
                  className={asFormField ? (formFill(cellColour) ? FORM_VALUE_PILL : FORM_VALUE) : GRID_VALUE}
                  style={{
                    backgroundColor: asFormField
                      ? formFill(cellColour)
                      : cellColour
                        ? themed.fill(cellColour.backgroundColour)
                        : 'inherit',
                    color: cellColour ? themed.text(cellColour.fontColour, cellColour.backgroundColour) : 'inherit',
                    fontWeight: cellColour ? (cellColour.fontBold ? 'bold' : 'normal') : 'normal',
                    fontStyle: cellColour ? (cellColour.fontItalic ? 'italic' : 'normal') : 'normal',
                    textDecoration: cellColour ? (cellColour.fontUnderline ? 'underline' : 'none') : 'none'
                  }}
                >
                  {currentValue != null ? currentValue.toLocaleString() : ''}
                </span>
              )
            })()}
          </span>
        ) : (
          isArray && (
            <div
              id="select-value-list"
              className={styles.content({
                validation,
                class: 'flex flex-wrap p-0'
              })}
              style={{
                height:
                  selected && isExpanded
                    ? 'auto'
                    : (currentValue as string[]).length <= (rowHeight ? rowHeight * 3 : 1)
                      ? 'fit-content'
                      : `${getRowHeightVariable(rowHeight) - 3}px`
              }}
            >
              {(currentValue as string[]).map((val, index) => {
                const cellColour = cellColours?.get(val) ?? null
                return (
                  <div
                    key={`${index}-${val}`}
                    id="select-value-item"
                    data-testid="select-value-item"
                    className={asFormField ? FORM_VALUE_PILL : GRID_VALUE}
                    style={{
                      backgroundColor: asFormField
                        ? formFill(cellColour)
                        : cellColour
                          ? themed.fill(cellColour.backgroundColour)
                          : 'inherit',
                      color: cellColour ? themed.text(cellColour.fontColour, cellColour.backgroundColour) : 'inherit',
                      fontWeight: cellColour ? (cellColour.fontBold ? 'bold' : 'normal') : 'normal',
                      fontStyle: cellColour ? (cellColour.fontItalic ? 'italic' : 'normal') : 'normal',
                      textDecoration: cellColour ? (cellColour.fontUnderline ? 'underline' : 'none') : 'none'
                    }}
                  >
                    {val != null ? val.toLocaleString() : ''}
                  </div>
                )
              })}
            </div>
          )
        )}
        {asFormField && !readOnly && <ChevronDown aria-hidden className="ml-auto shrink-0 self-center text-muted" />}
      </div>

      {selectCellMenu.open && (
        <SelectMenu
          id="select-cell-menu"
          menuState={selectCellMenu}
          setMenuState={setSelectCellMenu}
          options={allOptions}
          groupBy={hasManualGroup}
          onOptionClick={(option) => handleOptionSelected(option)}
          width={menuWidth}
          multiselect={kind === 'multiselect'}
          // A single select's value is one option, not a list: wrapped, so its option shows as selected.
          optionsSelected={Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue as string] : []}
          onCreateOption={canCreateOption ? handleCreateOption : undefined}
          noOptionsMessage={noOptionsMessage()}
          optionWeight={asFormField ? 'normal' : 'medium'}
        />
      )}
    </div>
  )
}

export const SelectCell = SelectCellComponent
